import {
  USER_ATTRIBUTES,
  LINKEDIN_ATTRIBUTES,
  PORTFOLIO_ATTRIBUTES,
  SEARCH_CONFIG,
} from "../constants/searchConfig";
import { extractLinkedInVanity } from "@/app/components/utils/linkedinUtils";

export const createSearchParams = (searchType, query) => {
  const baseParams = {
    hitsPerPage: SEARCH_CONFIG.hitsPerPage,
  };

  switch (searchType) {
    case "email":
      return {
        ...baseParams,
        query: "",
        filters: `contacts.email:"${query.trim().toLowerCase()}"`,
        attributesToRetrieve: USER_ATTRIBUTES,
        typoTolerance: false,
        removeWordsIfNoResults: "none",
      };

    case "linkedin":
      const vanity = extractLinkedInVanity(query);
      if (vanity) {
        // Search by vanity AND all profile.linkedin_url variants — a portfolio may
        // have vanity empty/null if it was saved via the enrich route, so the URL
        // filter is the reliable fallback.
        const linkedinUrlVariants = [
          `https://www.linkedin.com/in/${vanity}`,
          `https://linkedin.com/in/${vanity}`,
          `linkedin.com/in/${vanity}`,
        ];
        return {
          ...baseParams,
          query: "",
          filters: [
            `vanity:"${vanity}"`,
            ...linkedinUrlVariants.map((u) => `profile.linkedin_url:"${u}"`),
          ].join(" OR "),
          attributesToRetrieve: LINKEDIN_ATTRIBUTES,
          typoTolerance: false,
          exactOnSingleWordQuery: "attribute",
          removeWordsIfNoResults: "none",
        };
      }

      // Handle full LinkedIn URLs with multiple format variations
      if (query.includes("linkedin.com/in/")) {
        // Normalize the URL for searching
        const normalizedUrl = query
          .replace(/^https?:\/\//, "")
          .replace(/^www\./, "")
          .replace(/\/$/, "")
          .split("?")[0]
          .split("#")[0];

        // Try multiple URL formats that might be stored
        const urlVariations = [
          query, // Original query
          `https://www.linkedin.com/in/${vanity}`,
          `https://linkedin.com/in/${vanity}`,
          `linkedin.com/in/${vanity}`,
          normalizedUrl,
        ].filter(Boolean);

        return {
          ...baseParams,
          query: "",
          filters: urlVariations
            .map((url) => `profile.linkedin_url:"${url}"`)
            .join(" OR "),
          attributesToRetrieve: LINKEDIN_ATTRIBUTES,
          typoTolerance: false,
        };
      }

      return {
        ...baseParams,
        query,
        attributesToRetrieve: LINKEDIN_ATTRIBUTES,
        restrictSearchableAttributes: ["profile.linkedin_url"],
        typoTolerance: false,
        exactOnSingleWordQuery: "attribute",
        removeWordsIfNoResults: "none",
      };

    case "company":
      return {
        ...baseParams,
        query,
        attributesToRetrieve: USER_ATTRIBUTES,
        restrictSearchableAttributes: [
          "company.name",
          "profile.affiliation_name",
        ],
        typoTolerance: true,
        minWordSizefor1Typo: SEARCH_CONFIG.minWordSizefor1Typo,
        minWordSizefor2Typos: SEARCH_CONFIG.minWordSizefor2Typos,
      };

    case "name":
      return {
        ...baseParams,
        query,
        attributesToRetrieve: USER_ATTRIBUTES,
        restrictSearchableAttributes: [
          "profile.given_name",
          "profile.family_name",
        ],
        typoTolerance: true,
        minWordSizefor1Typo: SEARCH_CONFIG.minWordSizefor1Typo,
        minWordSizefor2Typos: SEARCH_CONFIG.minWordSizefor2Typos,
        queryType: "prefixAll",
      };
  }
};

export const createPortfolioSearchParams = (searchParams) => {
  const updatedParams = {
    ...searchParams,
    attributesToRetrieve: PORTFOLIO_ATTRIBUTES,
  };

  if (
    searchParams.restrictSearchableAttributes?.includes(
      "profile.affiliation_name",
    )
  ) {
    updatedParams.restrictSearchableAttributes =
      searchParams.restrictSearchableAttributes.filter(
        (attr) => attr !== "profile.affiliation_name",
      );

    if (updatedParams.restrictSearchableAttributes.length === 0) {
      delete updatedParams.restrictSearchableAttributes;
    }
  }

  if (searchParams.restrictSearchableAttributes?.includes("contacts.email")) {
    updatedParams.restrictSearchableAttributes = ["contacts.email"];
  }

  return updatedParams;
};

// Stop words stripped from raw user input before sending to Algolia.
// Includes common location noise words so "CEO of ExampleCo in USA" → "CEO ExampleCo".
const DISCOVER_STOP_WORDS = new Set([
  "of",
  "at",
  "in",
  "the",
  "a",
  "an",
  "for",
  "and",
  "or",
  "with",
  "who",
  "is",
  "are",
  "find",
  "looking",
  "need",
  "want",
  "me",
  "from",
  "based",
  // Country / region codes — keep the short ones that are unambiguously
  // locations and not part of any company name (US, UK, EU, UAE, etc.)
  "usa",
  "us",
  "uk",
  "eu",
  "uae",
  "nyc",
  "ny",
  "la",
  "sf",
  "dc",
  "ca",
  "america",
  "europe",
  "asia",
  "africa",
  "worldwide",
  "global",
  "remote",
  // Generic skill-level qualifiers — these add no Algolia search value;
  // what matters is the skill/tool name itself (e.g. "expert in Bubble" → "Bubble")
  "expert",
  "experienced",
  "specialist",
  "skilled",
  "certified",
  "professional",
  "passionate",
  "seasoned",
  "proficient",
]);

/**
 * Strip stop words from a raw user query to get clean Algolia search terms.
 * e.g. "CEO of Meex" → "CEO Meex"
 */
export const buildAlgoliaDiscoverQuery = (rawQuery) =>
  rawQuery
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 1 && !DISCOVER_STOP_WORDS.has(w.toLowerCase()))
    .join(" ");

/**
 * Discover search params: full-text searches across expertise/intro/about/company.
 * Accepts the already-cleaned Algolia query string (call buildAlgoliaDiscoverQuery first).
 *
 * @param {string} query - cleaned query, e.g. "CEO Meex"
 */
export const createDiscoverSearchParams = (query) => {
  return {
    hitsPerPage: 3,
    attributesToRetrieve: USER_ATTRIBUTES,
    query,
    restrictSearchableAttributes: [
      "profile.expertise",
      "profile.intro",
      "profile.about",
      "linkedin.headline",
      "linkedin.summary",
      "linkedin.title",
      "company.name",
      "profile.affiliation_name",
    ],
    typoTolerance: true,
    // Require 7+ chars before allowing a 1-typo match — prevents short proper
    // nouns like "Bubble" from matching "Hubble" (6 chars, 1 typo difference).
    minWordSizefor1Typo: 7,
    minWordSizefor2Typos: SEARCH_CONFIG.minWordSizefor2Typos,
  };
};

export const createPortfolioDiscoverSearchParams = (discoverParams) => ({
  ...discoverParams,
  attributesToRetrieve: PORTFOLIO_ATTRIBUTES,
  // portfolios_search doesn't have profile.affiliation_name as a searchable
  // attribute — remove it to avoid an Algolia "attribute not in searchableAttributes" error
  restrictSearchableAttributes: (
    discoverParams.restrictSearchableAttributes || []
  ).filter((a) => a !== "profile.affiliation_name"),
});

export const getIndexNames = () => ({
  users: process.env.NEXT_PUBLIC_ALGOLIA_USERS_INDEX,
  portfolios: process.env.NEXT_PUBLIC_ALGOLIA_PORTFOLIOS_INDEX,
  industries:
    process.env.NEXT_PUBLIC_ALGOLIA_INDUSTRIES_INDEX || "industries_search",
});
